import { useRef, useState } from 'react';
import { Box, chakra, Flex, Text } from '@chakra-ui/react';
import { AiField } from './AiField';
import { RichTextField } from './RichTextField';

/**
 * Editor for a list of strings (service includes, tier features, marquee
 * items, paragraphs, …). Rows are reordered by dragging the grip handle on
 * the left; each row is AI-enhanceable and removable.
 */
export function StringListEditor({
  label,
  items,
  onChange,
  context,
  multiline = false,
  /** Prose rows (paragraphs) get the rich text toolbar instead of a plain textarea. Implies multiline. */
  rich = false,
  addLabel = '+ Add item',
  baseline,
}: {
  label?: string;
  items: string[];
  onChange: (next: string[]) => void;
  context?: string;
  multiline?: boolean;
  rich?: boolean;
  addLabel?: string;
  /** Published list to diff against — rows that differ are highlighted green. */
  baseline?: string[];
}) {
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  // Rows are draggable only while the pointer is on the grip, so text
  // selection inside the inputs keeps working.
  const dragArmed = useRef(false);

  const update = (index: number, value: string) =>
    onChange(items.map((item, i) => (i === index ? value : item)));
  const remove = (index: number) => onChange(items.filter((_, i) => i !== index));

  const moveTo = (from: number, to: number) => {
    if (from === to) return;
    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onChange(next);
    setDragIndex(to);
  };

  return (
    <Box w="100%">
      {label && (
        <Text fontSize="0.66rem" fontWeight="600" letterSpacing="0.14em" textTransform="uppercase" color="brand.ink" mb="0.5rem">
          {label}
        </Text>
      )}
      <Flex direction="column" gap="0.5rem">
        {items.map((item, index) => (
          <Flex
            key={index}
            gap="0.4rem"
            align={multiline || rich ? 'flex-start' : 'center'}
            opacity={dragIndex === index ? 0.45 : 1}
            draggable={dragIndex === index || undefined}
            onDragStart={(event) => {
              if (!dragArmed.current) {
                event.preventDefault();
                return;
              }
              event.dataTransfer.effectAllowed = 'move';
              setDragIndex(index);
            }}
            onDragOver={(event) => {
              event.preventDefault();
              if (dragIndex !== null && dragIndex !== index) moveTo(dragIndex, index);
            }}
            onDrop={(event) => event.preventDefault()}
            onDragEnd={() => {
              setDragIndex(null);
              dragArmed.current = false;
            }}
          >
            <Box
              title="Drag to reorder"
              aria-label="Drag to reorder"
              cursor="grab"
              _active={{ cursor: 'grabbing' }}
              color="ink.light"
              fontSize="0.85rem"
              lineHeight="1"
              px="0.15rem"
              py={multiline || rich ? '0.7rem' : '0'}
              alignSelf={multiline || rich ? 'flex-start' : 'center'}
              userSelect="none"
              _hover={{ color: 'brand.mid' }}
              onMouseDown={(event) => {
                dragArmed.current = true;
                // Arm the parent row for dragging.
                (event.currentTarget.parentElement as HTMLElement).setAttribute('draggable', 'true');
              }}
              onMouseUp={(event) => {
                dragArmed.current = false;
                (event.currentTarget.parentElement as HTMLElement).removeAttribute('draggable');
              }}
            >
              ⠿
            </Box>
            <Box flex="1">
              {rich ? (
                <RichTextField
                  value={item}
                  onChange={(next) => update(index, next)}
                  context={context}
                  baseline={baseline ? (baseline[index] ?? '') : undefined}
                />
              ) : (
                <AiField
                  value={item}
                  onChange={(next) => update(index, next)}
                  context={context}
                  multiline={multiline}
                  baseline={baseline ? (baseline[index] ?? '') : undefined}
                />
              )}
            </Box>
            <chakra.button
              type="button"
              onClick={() => remove(index)}
              title="Remove"
              bg="transparent"
              border="1px solid"
              borderColor="paper.linen"
              color="brand.terracotta"
              fontSize="0.7rem"
              w="24px"
              h="24px"
              flexShrink={0}
              mt={multiline || rich ? '0.5rem' : '0'}
              borderRadius="6px"
              cursor="pointer"
              _hover={{ borderColor: 'brand.terracotta' }}
            >
              ✕
            </chakra.button>
          </Flex>
        ))}
      </Flex>
      <chakra.button
        type="button"
        onClick={() => onChange([...items, ''])}
        mt="0.5rem"
        bg="transparent"
        border="1px dashed"
        borderColor="brand.pale"
        color="brand.mid"
        fontSize="0.72rem"
        fontWeight="500"
        letterSpacing="0.08em"
        px="1rem"
        py="0.45rem"
        borderRadius="10px"
        cursor="pointer"
        _hover={{ bg: 'brand.wash' }}
      >
        {addLabel}
      </chakra.button>
    </Box>
  );
}
